/**
 * Canonical presentation layer for the engine's `assumptions` array.
 * The results panel, the read-only shared estimate and the PDF export all use
 * these helpers, so the three surfaces can never drift.
 */

/**
 * Assumptions that can be edited inline. An edit substitutes the modelled
 * default for THIS estimate only: the published engine constant never changes
 * and the value becomes a user planning input, not a verified market rate.
 * Purely derived rows (audit days, duration, planning range) stay read-only.
 */
export const ASSUMPTION_EDITORS = {
  effectivePersonnel: { field: 'employees', type: 'number', min: 1, max: 1000000, step: 1 },
  locations: { field: 'locations', type: 'number', min: 1, max: 500, step: 1 },
  infrastructure: { field: 'infraType', type: 'select', options: ['cloud', 'hybrid', 'onpremise'], labelPrefix: 'calcData.infra' },
  maturityLevel: { field: 'overallMaturity', type: 'number', min: 1, max: 5, step: 0.1, extra: { maturityMode: 'simple' } },
  deliveryModel: { field: 'mixRatio', type: 'number', min: 0, max: 100, step: 1, suffix: '%', extra: { approach: 'mixed' } },
  internalRate: { field: 'internalRate', type: 'currency', min: 0, max: 1000000, step: 10 },
  consultantRate: { field: 'consultantRate', type: 'currency', min: 0, max: 1000000, step: 10 },
  certBodyDayRate: { field: 'certBodyDayRate', type: 'currency', min: 0, max: 1000000, step: 10, resettable: true },
  trainingCost: { field: 'trainingCost', type: 'currency', min: 0, max: 50000000, step: 100, resettable: true },
  toolsCost: { field: 'toolsCost', type: 'currency', min: 0, max: 50000000, step: 100, resettable: true },
};

/** Config patch for an edited assumption (null clears an override). */
export function buildAssumptionPatch(id, rawValue) {
  const editor = ASSUMPTION_EDITORS[id];
  if (!editor) return null;
  if (rawValue === null) return { ...(editor.extra || {}), [editor.field]: null };

  let value = rawValue;
  if (editor.type !== 'select') {
    value = Number(rawValue);
    if (!Number.isFinite(value)) return null;
    value = Math.min(editor.max, Math.max(editor.min, value));
  }
  const patch = { ...(editor.extra || {}), [editor.field]: value };
  // The engine accepts either key for the delivery mix — keep them in step.
  if (editor.field === 'mixRatio') patch.internalSplit = value;
  return patch;
}

/**
 * The recurring TCO assumptions the user actually overrode.
 * Saved estimates always store the full resolved set, so provenance is only
 * honest if the override keys travel with the values.
 */
export function tcoOverridesOf(estimate) {
  const keys = estimate?.tco_overrides || [];
  const values = estimate?.tco_assumptions || {};
  if (keys.length === 0) return undefined;
  return Object.fromEntries(keys.filter(key => values[key] !== undefined).map(key => [key, values[key]]));
}

export function summarizeAssumptions(assumptions = []) {
  const defaults = assumptions.filter(a => a.provenance === 'DEFAULT_ASSUMPTION').length;
  const userInputs = assumptions.filter(a => a.provenance === 'USER_PROVIDED').length;
  return { defaults, userInputs, total: assumptions.length };
}

export function formatAssumptionValue(assumption, { t, formatCost }) {
  const { id, value } = assumption;
  const day = t('methodologyPage.unitDays');

  switch (id) {
    case 'effectivePersonnel':
    case 'locations':
      return String(value);
    case 'infrastructure':
      return t(`calcData.infra.${value}`);
    case 'maturityLevel':
      return `${t('methodologyPage.level')} ${value}`;
    case 'controlReuse':
      return value > 0 ? `−${value}%` : '0%';
    case 'deliveryModel':
      return `${value}% ${t('assumptions.internalShare')}`;
    case 'internalRate':
    case 'consultantRate':
    case 'certBodyDayRate':
      return `${formatCost(value)}/${day}`;
    case 'auditDays':
      return `${value} ${day}`;
    case 'trainingCost':
    case 'toolsCost':
      return formatCost(value);
    case 'durationWeeks':
      return `${value} ${t('estimates.weeks')}`;
    case 'planningRange':
      return `${formatCost(value?.[0])} – ${formatCost(value?.[1])}`;
    default:
      return String(value);
  }
}

export function buildAssumptionRows(assumptions = [], { t, formatCost }) {
  return assumptions.map(a => ({
    id: a.id,
    provenance: a.provenance,
    label: t(`assumptions.labels.${a.id}`),
    value: formatAssumptionValue(a, { t, formatCost }),
  }));
}
